import React, { useState, useEffect } from "react";
import {
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Button,
  MenuItem,
  Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Groups, HelpOutline } from "@mui/icons-material/";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useDualMsal } from "../contexts/MsalContext";
// A screening import's one-time links are lost on signing out: ask first.
import { confirmSignOut } from "../utilities/screeningImportStore";
import sharedConfig from "../../shared-config.json";

const menuIcons = {
  "Contact us": <Groups />,
  FAQ: <HelpOutline />,
};

function RelmedAppBar({ onLoginClick }) {
  const [pages, setPages] = useState(["FAQ", "Contact us"]);
  const [anchorElNav, setAnchorElNav] = useState(null);
  const { isAuthenticated, currentUser, logout } = useDualMsal();
  const theme = useTheme();
  const navigate = useNavigate();

  // Update menu when authentication state changes
  useEffect(() => {
    // FAQ and Contact content lives on the home page now, so only
    // surface Home + role-specific destinations in the nav.
    const pages = ["Home"];

    if (isAuthenticated && currentUser) {
      // Check if this is a B2C or Entra account
      const isB2CAccount = currentUser.accountType === "B2C";
      const hasRoles = currentUser.roles.length > 0;

      // Add My Relmed ONLY for participants (B2C accounts or Entra accounts with participant role)
      // AND only if they are NOT an admin or PI
      const isAdminOrPI =
        currentUser.roles.includes(sharedConfig.roles.admin.name) ||
        currentUser.roles.includes(sharedConfig.roles.pi.name);
      if (
        (isB2CAccount ||
          (hasRoles &&
            currentUser.roles.includes(sharedConfig.roles.participant.name))) &&
        !isAdminOrPI
      ) {
        pages.push("My Tasks");
      }

      // Add Administer for admin/PI roles (Entra accounts only)
      if (hasRoles && isAdminOrPI) {
        pages.push("Administer");
      }
    }

    setPages(pages);
  }, [isAuthenticated, currentUser]);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleMenuItemClick = (menuItem) => {
    switch (menuItem) {
      case "Contact us":
        navigate("/contact");
        break;
      case "FAQ":
        navigate("/faq");
        break;
      case "My Tasks":
        navigate("/participant/");
        break;
      case "Administer":
        navigate("/admin/");
        break;
      default:
        navigate("/");
        break;
    }
    handleCloseNavMenu();
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  return (
    <Box
      sx={{
        background: theme.palette.primary.light,
        color: theme.palette.text.primary,
      }}
    >
      <Toolbar sx={{ maxWidth: "xl", margin: "auto" }}>
        <Box
          sx={{
            flex: "1 1 0",
            display: { md: "flex", sm: "none", xs: "none" },
          }}
        >
          {isAuthenticated && (
            <Chip
              color="primary"
              label="Log out"
              onClick={() => {
                if (!confirmSignOut()) return;
                logout();
              }}
            />
          )}
          {!isAuthenticated && (
            <Chip color="primary" label="Log in" onClick={onLoginClick} />
          )}
        </Box>
        <Box sx={{ flexGrow: 1, display: { sm: "flex", md: "none" } }}>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleOpenNavMenu}
            color="inherit"
          >
            <MenuIcon />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorElNav}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            sx={{
              display: { xs: "block", md: "none" },
            }}
          >
            {pages.map((page) => (
              <MenuItem key={page} onClick={() => handleMenuItemClick(page)}>
                {menuIcons[page]}
                <Typography sx={{ ml: 1 }} textAlign="center">
                  {page}
                </Typography>
              </MenuItem>
            ))}
            <MenuItem key={"loginout"}>
              {isAuthenticated && (
                <Chip
                  sx={{ width: "100%" }}
                  color="primary"
                  label="Log out"
                  onClick={() => {
                    if (!confirmSignOut()) return;
                    logout();
                  }}
                />
              )}
              {!isAuthenticated && (
                <Chip
                  sx={{ width: "100%" }}
                  color="primary"
                  label="Log in"
                  onClick={onLoginClick}
                />
              )}
            </MenuItem>
          </Menu>
        </Box>
        <Typography
          variant="h5"
          noWrap
          component="a"
          href=""
          sx={{
            mr: 2,
            display: { xs: "flex", md: "none" },
            flexGrow: 1,
            fontWeight: 700,
            letterSpacing: ".3rem",
            color: theme.palette.secondary.main,
            textDecoration: "none",
          }}
        ></Typography>
        <Box
          sx={{
            flexGrow: 1,
            display: { md: "flex", sm: "none", xs: "none" },
            justifyContent: "center",
          }}
        >
          {pages.map((page) => (
            <Button
              key={page}
              onClick={() => handleMenuItemClick(page)}
              sx={{
                mx: 1,
                display: "flex",
                textTransform: "none",
              }}
            >
              {menuIcons[page]}
              <Typography
                sx={{ ml: 1, color: theme.palette.text.primary }}
                textAlign="center"
              >
                {page}
              </Typography>
            </Button>
          ))}
        </Box>
        <Box
          sx={{
            height: 64,
            display: "flex",
            justifyContent: "flex-end",
            flex: "1 1 0",
            alignItems: "flex-end",
          }}
        >
          <img
            src="/logo.png"
            alt="PRADAM"
            style={{
              height: 32,
              maxHeight: "100%",
              width: "auto",
              display: "block",
            }}
          />
        </Box>
      </Toolbar>
    </Box>
  );
}
export default RelmedAppBar;
